import ApiURL from '../Api/Api';

export const DEFAULT_COMPANY_THEME = '#0f66bb';

const clampHex = (value) => {
  const color = String(value || '').trim();
  return /^#[0-9a-f]{6}$/i.test(color) ? color.toLowerCase() : DEFAULT_COMPANY_THEME;
};

const hexToRgb = (hex) => {
  const clean = clampHex(hex).replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
};

const mix = (hex, target, amount) => {
  const base = hexToRgb(hex);
  const to = hexToRgb(target);
  const next = {
    r: Math.round(base.r + (to.r - base.r) * amount),
    g: Math.round(base.g + (to.g - base.g) * amount),
    b: Math.round(base.b + (to.b - base.b) * amount),
  };
  return `#${[next.r, next.g, next.b].map((part) => part.toString(16).padStart(2, '0')).join('')}`;
};

export const resolveLogoUrl = (logoPath) => {
  if (!logoPath) return '';
  if (/^https?:\/\//i.test(logoPath) || logoPath.startsWith('data:')) return logoPath;
  return `${ApiURL.replace(/\/$/, '')}/${String(logoPath).replace(/^\/+/, '')}`;
};

export const getStoredCompanyBranding = () => {
  try {
    return JSON.parse(localStorage.getItem('company_branding') || 'null') || {};
  } catch {
    return {};
  }
};

export const applyCompanyTheme = (branding = getStoredCompanyBranding()) => {
  const color = clampHex(branding.theme_color);
  const root = document.documentElement;
  root.style.setProperty('--brand', color);
  root.style.setProperty('--brand-50', mix(color, '#ffffff', 0.92));
  root.style.setProperty('--brand-100', mix(color, '#ffffff', 0.84));
  root.style.setProperty('--brand-200', mix(color, '#ffffff', 0.68));
  root.style.setProperty('--brand-600', color);
  root.style.setProperty('--brand-700', mix(color, '#000000', 0.14));
  root.style.setProperty('--brand-ring', `${hexToRgb(color).r} ${hexToRgb(color).g} ${hexToRgb(color).b}`);
};

export const storeCompanyBranding = (branding) => {
  const normalized = {
    company_name: branding?.company_name || '',
    theme_color: clampHex(branding?.theme_color),
    logo_path: branding?.logo_path || '',
  };
  localStorage.setItem('company_branding', JSON.stringify(normalized));
  localStorage.setItem('company_name', normalized.company_name);
  localStorage.setItem('company_theme_color', normalized.theme_color);
  localStorage.setItem('company_logo_path', normalized.logo_path);
  applyCompanyTheme(normalized);
  window.dispatchEvent(new Event('company-branding-updated'));
  return normalized;
};

export const compressLogoFile = (file, maxSize = 512, quality = 0.82) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Company logo is required'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      reject(new Error('Logo must be an image file'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to read logo image'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('Unable to process logo image'));
      image.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
